import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { 
  validateStorageConnection, 
  createPersonalStorageConnection, 
  createOrganizationStorageConnection,
  getUserOrganizations,
  type OrganizationData 
} from '@/utils/storageIsolation';
import { HardDrive, Users, Folder, AlertTriangle, CheckCircle } from 'lucide-react';

interface StorageConnectionWizardProps {
  onConnectionComplete: () => void;
  onSkip?: () => void;
}

export const StorageConnectionWizard = ({ onConnectionComplete, onSkip }: StorageConnectionWizardProps) => {
  const { toast } = useToast();
  const [step, setStep] = useState<'type' | 'personal' | 'organization'>('type');
  const [connectionType, setConnectionType] = useState<'personal' | 'organization'>('personal');
  const [organizations, setOrganizations] = useState<OrganizationData[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [folderHandle, setFolderHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [folderPath, setFolderPath] = useState<string>('');
  const [folderName, setFolderName] = useState<string>('GreenCard_Data');
  const [isConnecting, setIsConnecting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkExistingConnection();
    loadOrganizations();
  }, []);

  const checkExistingConnection = async () => {
    const validation = await validateStorageConnection();
    if (validation.isConnected) {
      onConnectionComplete();
      return;
    }
    setLoading(false);
  };

  const loadOrganizations = async () => {
    const { organizations, error } = await getUserOrganizations();
    if (error) {
      console.error('Failed to load organizations:', error);
    } else {
      setOrganizations(organizations);
    }
  };

  const selectFolder = async () => {
    try {
      if ('showDirectoryPicker' in window) {
        const handle = await (window as any).showDirectoryPicker({
          mode: 'readwrite',
          startIn: 'documents'
        });
        setFolderHandle(handle);
        setFolderPath(handle.name);
        
        toast({
          title: "Folder Selected",
          description: `Selected: ${handle.name}`,
        });
      } else {
        toast({
          title: "Not Supported",
          description: "Your browser doesn't support folder selection. Please use a modern browser.",
          variant: "destructive",
        });
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        toast({
          title: "Selection Failed",
          description: "Could not access the selected folder.",
          variant: "destructive",
        });
      }
    }
  };

  const testStorageConnection = async (handle: FileSystemDirectoryHandle): Promise<boolean> => {
    try {
      // Test write access
      const testFile = await handle.getFileHandle('test-connection.txt', { create: true });
      const writable = await testFile.createWritable();
      await writable.write('GreenCard ESG - Connection Test');
      await writable.close();
      
      // Test read access
      const file = await testFile.getFile();
      const content = await file.text();
      
      if (!content.includes('GreenCard ESG')) {
        throw new Error('Failed to verify file content');
      }
      
      // Clean up test file
      await handle.removeEntry('test-connection.txt');
      
      return true;
    } catch (error) {
      console.error('Storage connection test failed:', error);
      throw new Error('Cannot read/write to the selected folder. Please check permissions.');
    }
  };

  const handlePersonalConnection = async () => {
    if (!folderHandle || !folderPath) {
      toast({
        title: "Folder Required",
        description: "Please select a folder for your data storage.",
        variant: "destructive",
      });
      return;
    }

    setIsConnecting(true);
    
    try {
      // Test the connection first
      await testStorageConnection(folderHandle);

      const storageConfig = {
        provider: 'local-folder',
        folderPath,
        folderName,
        folderHandle,
        storageType: 'local-folder',
        setupDate: new Date().toISOString()
      };

      const result = await createPersonalStorageConnection(storageConfig);
      
      if (result.success) {
        toast({
          title: "Storage Connected",
          description: "Your personal storage has been configured successfully.",
        });
        
        // Verify the connection one more time
        const validation = await validateStorageConnection();
        if (validation.isConnected) {
          onConnectionComplete();
        } else {
          throw new Error('Storage connection verification failed');
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        title: "Connection Failed",
        description: error instanceof Error ? error.message : "Failed to connect storage",
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleOrganizationConnection = async () => {
    if (!selectedOrg) {
      toast({
        title: "Organization Required",
        description: "Please select an organization.",
        variant: "destructive",
      });
      return;
    }

    if (!folderHandle || !folderPath) {
      toast({
        title: "Folder Required",
        description: "Please select the organization's shared folder.",
        variant: "destructive",
      });
      return;
    }

    setIsConnecting(true);

    try {
      // Test the connection first
      await testStorageConnection(folderHandle);

      const storageConfig = {
        provider: 'organization-folder',
        folderPath,
        folderName,
        folderHandle,
        storageType: 'organization-shared',
        organizationId: selectedOrg,
        setupDate: new Date().toISOString()
      };

      const result = await createOrganizationStorageConnection(selectedOrg, storageConfig);
      
      if (result.success) {
        toast({
          title: "Organization Storage Connected",
          description: "You're now connected to your organization's shared storage.",
        });
        
        // Verify the connection one more time
        const validation = await validateStorageConnection();
        if (validation.isConnected) {
          onConnectionComplete();
        } else {
          throw new Error('Organization storage connection verification failed');
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({
        title: "Connection Failed",
        description: error instanceof Error ? error.message : "Failed to connect to organization storage",
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (step === 'type') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-6 w-6" />
            Connect Your Data Storage
          </CardTitle>
          <p className="text-muted-foreground">
            Choose how you want to store and access your ESG data
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Storage Required:</strong> You must connect storage before using the application. 
              Your data will be stored locally and never automatically synced to our servers.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card 
              className={`cursor-pointer transition-colors hover:bg-accent ${connectionType === 'personal' ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setConnectionType('personal')}
            >
              <CardContent className="p-6 text-center">
                <HardDrive className="h-12 w-12 mx-auto mb-4 text-primary" />
                <h3 className="font-semibold mb-2">Personal Storage</h3>
                <p className="text-sm text-muted-foreground">
                  Store data in your own local folder. Perfect for individual use.
                </p>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-colors hover:bg-accent ${connectionType === 'organization' ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setConnectionType('organization')}
            >
              <CardContent className="p-6 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-primary" />
                <h3 className="font-semibold mb-2">Organization Storage</h3>
                <p className="text-sm text-muted-foreground">
                  Connect to your team's shared storage for collaboration.
                </p>
                {organizations.length === 0 && (
                  <p className="text-xs text-orange-600 mt-2">
                    No organizations available
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex gap-3">
            {onSkip && (
              <Button variant="outline" onClick={onSkip} className="flex-1">
                Skip Setup
              </Button>
            )}
            <Button 
              onClick={() => setStep(connectionType)}
              disabled={connectionType === 'organization' && organizations.length === 0}
              className="flex-1"
            >
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'personal') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-6 w-6" />
            Setup Personal Storage
          </CardTitle>
          <p className="text-muted-foreground">
            Select a folder on your computer to store your ESG data
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="folderName">Data Folder Name</Label>
            <Input
              id="folderName"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="GreenCard_Data"
            />
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <Folder className="h-4 w-4" />
              Select Storage Location
            </Label>
            <div className="flex gap-2 items-center mt-2">
              <Input
                value={folderPath || 'No folder selected'}
                readOnly
                placeholder="Click 'Choose Folder' to select location"
                className="flex-1"
              />
              <Button variant="outline" onClick={selectFolder}>
                Choose Folder
              </Button>
            </div>
            {folderPath && (
              <p className="text-sm text-green-600 mt-1">
                <CheckCircle className="h-4 w-4 inline mr-1" />
                Folder selected: {folderPath}
              </p>
            )}
          </div>

          <Alert>
            <AlertDescription>
              This folder will contain all your ESG data organized in subfolders. 
              The connection will be tested to ensure read/write access.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep('type')} className="flex-1">
              Back
            </Button>
            <Button 
              onClick={handlePersonalConnection}
              disabled={!folderPath || isConnecting}
              className="flex-1"
            >
              {isConnecting ? 'Testing Connection...' : 'Connect & Test Storage'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'organization') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-6 w-6" />
            Connect to Organization Storage
          </CardTitle>
          <p className="text-muted-foreground">
            Connect to your team's shared storage location
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="organization">Select Organization</Label>
            <Select value={selectedOrg} onValueChange={setSelectedOrg}>
              <SelectTrigger>
                <SelectValue placeholder="Choose your organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.organization_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <Folder className="h-4 w-4" />
              Organization's Shared Folder
            </Label>
            <div className="flex gap-2 items-center mt-2">
              <Input
                value={folderPath || 'No folder selected'}
                readOnly
                placeholder="Select the organization's shared folder"
                className="flex-1"
              />
              <Button variant="outline" onClick={selectFolder}>
                Choose Folder
              </Button>
            </div>
            {folderPath && (
              <p className="text-sm text-green-600 mt-1">
                <CheckCircle className="h-4 w-4 inline mr-1" />
                Connected to: {folderPath}
              </p>
            )}
          </div>

          <Alert>
            <AlertDescription>
              Make sure you're selecting the correct shared folder provided by your organization admin. 
              The connection will be tested to ensure proper access.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep('type')} className="flex-1">
              Back
            </Button>
            <Button 
              onClick={handleOrganizationConnection}
              disabled={!selectedOrg || !folderPath || isConnecting}
              className="flex-1"
            >
              {isConnecting ? 'Testing Connection...' : 'Connect & Test Organization Storage'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};
